/**
 * Visibilità di gruppo holding sulle "config entities" (BE v1.24.0).
 *
 * Attributo `scope` ('tenant' = locale | 'group' = condiviso nel gruppo holding).
 * Presente su: project-type-presets, tardiness-policies, project-attribute-definitions,
 * project-categories. Un record di gruppo di un ALTRO tenant è visibile ma read-only
 * (solo il proprietario può modificarlo/eliminarlo → BE 403 GROUP_RECORD_READONLY).
 * Creare/portare a 'group' richiede claim canManageSubTenants + pool di gruppo attivo.
 * A parità di chiave logica, il record locale prevale (dedup FE-side, "local wins").
 */

export type GroupScope = 'tenant' | 'group';

export const GROUP_SCOPE_OPTIONS: { value: GroupScope; label: string }[] = [
  { value: 'tenant', label: 'Locale' },
  { value: 'group', label: 'Gruppo' },
];

/**
 * PERCHÉ «GRUPPO» PUÒ ESSERE SPENTO — le **tre** condizioni, in una frase sola.
 *
 * ⚠️ Fino al 19-09-2026 questa frase era scritta a mano in quattro schermate e nel catalogo
 * errori, e diceva **due** condizioni su tre: «serve il permesso di gestire le aziende sotto la tua e il
 * pool di gruppo attivo». La terza la pretende il server e non la diceva nessuno —
 * `TimesheetPolicyResourceDefinition.cs:176-182` chiama `HasDescendantsAsync` sul tenant del
 * token e risponde `422 …_GROUP_SCOPE_NOT_ALLOWED` a un'azienda **senza aziende figlie**.
 *
 * Il risultato era che a un'azienda senza controllate l'opzione veniva offerta, e il rifiuto
 * arrivava dopo il viaggio spiegando una cosa che il testo sotto il controllo non nominava. Un
 * avviso che elenca i requisiti e ne salta uno è peggio di uno generico: chi lo legge verifica i
 * due che conosce, li trova a posto, e conclude che sia rotto.
 *
 * ⚠️ Il nostro cancello lato client (`useCanCreateGroupScope`) controlla solo le prime due: la
 * terza costerebbe una lettura dell'albero dentro un hook che usano quattro pannelli. Finché è
 * così, **dirla** è il minimo che tiene onesto lo schermo — il 422 resta possibile, ma smette di
 * essere inspiegabile.
 */
export const GROUP_SCOPE_REQUISITI =
  'Per condividere nel gruppo servono due cose: il permesso di gestire le aziende sotto la tua, e almeno '
  + 'un’azienda figlia che possa ereditare la configurazione (oppure il pool di gruppo attivo).';

export function groupScopeLabel(scope?: string | null): string {
  return scope === 'group' ? 'Gruppo' : 'Locale';
}

/** Colore badge: locale neutro, gruppo evidenziato. */
export function groupScopeColor(scope?: string | null): string {
  return scope === 'group' ? 'teal' : 'gray';
}

/** Shape minima dei record con scope + relationship tenant. */
export interface ScopedRecord {
  scope?: string | null;
  tenant?: { id?: string } | null;
}

/** Il record appartiene al tenant corrente? (se il tenant non è incluso, lo consideriamo proprio). */
export function isOwnRecord(record: ScopedRecord, currentTenantId?: string | null): boolean {
  const tid = record.tenant?.id;
  return !tid || !currentTenantId || tid === currentTenantId;
}

/** Record di gruppo di un ALTRA società → read-only (edit/delete disabilitati). */
export function isForeignGroupRecord(record: ScopedRecord, currentTenantId?: string | null): boolean {
  return record.scope === 'group' && !isOwnRecord(record, currentTenantId);
}

/**
 * "Local wins": a parità di chiave logica tiene i record propri e scarta i record di
 * gruppo altrui che duplicano una chiave già coperta localmente. Usato dove la lista
 * alimenta una selezione (preset, definizioni campi custom), non nella gestione admin.
 */
export function dedupeLocalWins<T extends ScopedRecord>(
  rows: T[],
  keyFn: (row: T) => string | null | undefined,
  currentTenantId?: string | null,
): T[] {
  const localKeys = new Set(
    rows.filter((r) => isOwnRecord(r, currentTenantId)).map(keyFn).filter(Boolean),
  );
  return rows.filter((r) => isOwnRecord(r, currentTenantId) || !localKeys.has(keyFn(r)));
}
